import { Tooltip } from "@blueprintjs/core"
import cc from "classcat"
import React from "react"
import { useFormContext } from "react-hook-form"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./OtherExpensesRow.module.css"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"
import { OTHER_EXPENSES_ROLE, getAllocationMessage } from "~/helpers/otherCosts"

import { Delete, Warning } from "~/common/react-icons"

import ExpenseBudgetInput from "../ExpenseBudgetInput"
import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import Column from "../TableColumn"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"

const OtherExpensesRow = () => {
  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  // Get calculated values from context
  const { expensesAllocationDifference, hasExpenses } =
    useProjectBudgetFormContext()

  // Find the "Other Expenses" role
  const expenseRole = formData.roles.find(
    (role) => role.id === OTHER_EXPENSES_ROLE.id,
  )
  if (!expenseRole) return null

  const value = expenseRole.estimatedMinutes
  const canDelete = !expenseRole.isScheduled && !hasExpenses
  const expensesBudget = value

  const overAllocated = expensesAllocationDifference < 0
  const overOrBelowWarning =
    !!expensesBudget && expensesAllocationDifference !== 0

  const handleUpdate = (val: number) => {
    // Update the form's expensesBudget field for the GraphQL mutation
    setValue("expensesBudget", val)

    // Also update the role's estimatedMinutes for display/calculation purposes
    const updatedRoles = formData.roles.map((role) =>
      role.id === OTHER_EXPENSES_ROLE.id
        ? { ...role, estimatedMinutes: val }
        : role,
    )
    setValue("roles", updatedRoles)
  }

  const handleDelete = () => {
    if (!canDelete) return

    // Clear the form's expensesBudget field
    setValue("expensesBudget", 0)

    // Remove the role and add to deleted roles
    const newRoles = formData.roles.filter(
      (r) => r.id !== OTHER_EXPENSES_ROLE.id,
    )
    const newDeletedRoles = [...formData.deletedRoleIds, OTHER_EXPENSES_ROLE.id]
    setValue("roles", newRoles)
    setValue("deletedRoleIds", newDeletedRoles)
  }

  const deleteTooltip = canDelete
    ? "Remove budget allocation for other expenses"
    : "You cannot remove this from your budget because you already have other expenses recorded for this project."

  return (
    <Row dataTest="other-expenses-row" className={styles.otherExpensesWrapper}>
      <Column className={styles.otherExpensesLabel}>
        <span
          className={cc({
            [styles.otherExpensesWarning]: overAllocated && overOrBelowWarning,
          })}
        >
          Other Expenses
        </span>
        {overOrBelowWarning && (
          <Tooltip
            content={`${getAllocationMessage(expensesAllocationDifference)} ${formatCurrency(Math.abs(expensesAllocationDifference))}`}
            placement="top"
          >
            <Warning
              color="var(--warning)"
              data-test="budget-expenses-warning"
            />
          </Tooltip>
        )}
      </Column>
      <Column className={styles.otherExpensesInput}>
        <ExpenseBudgetInput
          name="other-expenses-budget_dollars"
          dataTest="other-expenses-budget_dollars"
          value={value}
          currency={currency}
          onUpdate={handleUpdate}
        />
      </Column>
      <div
        className={cc([
          commonStyles.deleteIconContainer,
          !canDelete && commonStyles.disabled,
        ])}
        data-test="budget-expenses-delete"
        onClick={handleDelete}
      >
        <Delete alt={deleteTooltip} color="var(--winter)" />
      </div>
    </Row>
  )
}

export default OtherExpensesRow
